import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import useSeo from "@/lib/useSeo";
import ComingSoon from "@/components/ComingSoon";
import { IMAGES, EASY_MASSAGE_URL } from "@/lib/assets";

const industries = [
  {
    name: "Easy Massage",
    tag: "Live now",
    live: true,
    href: EASY_MASSAGE_URL,
    image: IMAGES.massage,
    desc: "Beautiful, fully-bookable websites for massage studios and day spas. Online booking, therapist rostering, loyalty rewards, memberships, vouchers and marketing — all in one platform.",
  },
];

export default function Industries() {
  useSeo({
    title: "Industries | Easy Booking Solutions — Easy Massage & Easy Detailing",
    description:
      "Explore the industries powered by Easy Booking Solutions. Easy Massage is live now for massage studios and day spas, with Easy Detailing for car care coming soon.",
    path: "/industries",
  });

  return (
    <div data-testid="industries-page">
      <section className="px-6 md:px-12 pt-20 pb-12">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-[#3EA65B]">
            Our industries
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#0A0A0A] mt-4">
            One booking platform, tailored to your trade.
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed mt-6">
            We're rolling out Easy Booking across service industries. Each brand is fine-tuned for
            its trade — but powered by the same reliable booking engine.
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-7xl mx-auto space-y-10">
          {industries.map((ind, i) => (
            <motion.div
              key={ind.name}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-14 items-center bg-white border border-gray-100 rounded-3xl overflow-hidden ${
                i % 2 === 1 ? "lg:[direction:rtl]" : ""
              }`}
              data-testid={`industry-detail-${ind.name.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="relative h-72 lg:h-[420px] [direction:ltr]">
                <img
                  src={ind.image}
                  alt={ind.name}
                  className={`w-full h-full object-cover ${ind.live ? "" : "grayscale opacity-90"}`}
                />
                <span
                  className={`absolute top-5 left-5 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${
                    ind.live ? "bg-[#3EA65B] text-white" : "bg-white/90 text-gray-700"
                  }`}
                >
                  {!ind.live && <Clock className="h-3.5 w-3.5" />}
                  {ind.tag}
                </span>
              </div>
              <div className="p-8 lg:p-12 [direction:ltr]">
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A]">{ind.name}</h2>
                <p className="text-lg text-gray-600 leading-relaxed mt-4">{ind.desc}</p>
                {ind.live ? (
                  <a
                    href={ind.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`industries-visit-${ind.name.toLowerCase().replace(/\s/g, "-")}`}
                    className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#0A0A0A] text-white px-8 py-4 font-semibold hover:bg-gray-800 transition-all"
                  >
                    Visit {ind.name} <ArrowUpRight className="h-5 w-5" />
                  </a>
                ) : (
                  <Link
                    to="/contact"
                    data-testid="industries-notify"
                    className="inline-flex items-center gap-2 mt-8 rounded-full border-2 border-gray-200 text-[#0A0A0A] px-8 py-4 font-semibold hover:border-gray-900 transition-all"
                  >
                    Get notified at launch
                  </Link>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <ComingSoon />
    </div>
  );
}
